import type { EmissionsSankeyItem } from "../types/dashboard";
import type { LiveChartProjection } from "./liveChartContract";
import { emissionScopes } from "./emissionHierarchy";
import {
  resolveWidgetData,
  type ResolvedWidgetData,
} from "./resolveWidgetData";

/** Apply only the saved, validated transformation to fresh response data. */
export function projectWidgetData(
  source: string,
  raw: unknown,
  projection: LiveChartProjection,
): ResolvedWidgetData | null {
  const resolved = resolveWidgetData(source, raw);
  if (!resolved) return null;
  if (source === "allemissions" && projection.groupBy !== "hierarchy") {
    const scopes = emissionScopes(raw as EmissionsSankeyItem[]);
    const entries =
      projection.groupBy === "scope"
        ? scopes
        : scopes.flatMap((scope) =>
            projection.groupBy === "category"
              ? scope.categories
              : scope.categories.flatMap((category) =>
                  category.subcategories.length
                    ? category.subcategories
                    : [category],
                ),
          );
    const sums = new Map<string, number>();
    for (const entry of entries)
      sums.set(entry.code, (sums.get(entry.code) ?? 0) + entry.value);
    const ranked = [...sums].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
    );
    return {
      labels: ranked.map(([code]) => code),
      series: ranked.length
        ? [{ label: "Emissions", values: ranked.map(([, value]) => value) }]
        : [],
    };
  }
  let series = projection.series
    ? projection.series.flatMap((label) =>
        resolved.series.filter((row) => row.label === label),
      )
    : resolved.series;
  if (!series.length) return { labels: [], series: [] };
  if (projection.groupBy === "total")
    series = [
      {
        label: "Emissions",
        values: resolved.labels.map((_, i) =>
          series.reduce((sum, row) => sum + row.values[i], 0),
        ),
      },
    ];
  const count = projection.limit ?? resolved.labels.length;
  return {
    labels: resolved.labels.slice(0, count),
    series: series.map((row) => ({
      ...row,
      values: row.values.slice(0, count),
    })),
  };
}
